export const DEFAULT_EQUATION_LATEX =
  "ax^2 + bx + c = 0 \\\\\n\\iff x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}"

export type EquationTabId =
  | "basic"
  | "greek"
  | "calculus"
  | "trig"
  | "relations"
  | "matrices"

export type EquationSymbol = {
  id: string
  latex: string
  /** KaTeX source drawn on the button face. */
  display: string
  label: string
}

export type EquationTab = {
  id: EquationTabId
  label: string
  symbols: EquationSymbol[]
}

export const EQUATION_TABS: EquationTab[] = [
  {
    id: "basic",
    label: "Basic Math",
    symbols: [
      { id: "pow2", latex: "x^{2}", display: "x^{2}", label: "x squared" },
      { id: "pown", latex: "x^{}", display: "x^{n}", label: "x to the n" },
      { id: "sqrt", latex: "\\sqrt{}", display: "\\sqrt{x}", label: "Square root" },
      { id: "nthroot", latex: "\\sqrt[]{}", display: "\\sqrt[n]{x}", label: "Nth root" },
      { id: "frac", latex: "\\frac{}{}", display: "\\frac{a}{b}", label: "Fraction" },
      { id: "log", latex: "\\log_{}", display: "\\log_{a}", label: "Logarithm" },
      { id: "pi", latex: "\\pi", display: "\\pi", label: "Pi" },
      { id: "theta", latex: "\\theta", display: "\\theta", label: "Theta" },
      { id: "infty", latex: "\\infty", display: "\\infty", label: "Infinity" },
      { id: "pm", latex: "\\pm", display: "\\pm", label: "Plus minus" },
      { id: "paren", latex: "\\left(  \\right)", display: "\\left( \\, \\right)", label: "Parentheses" },
      { id: "brack", latex: "\\left[  \\right]", display: "\\left[ \\, \\right]", label: "Brackets" },
      { id: "abs", latex: "\\left|  \\right|", display: "\\left| x \\right|", label: "Absolute value" },
      { id: "fx", latex: "f(x)", display: "f(x)", label: "Function f(x)" },
      { id: "ln", latex: "\\ln", display: "\\ln", label: "Natural log" },
      { id: "exp", latex: "e^{}", display: "e^{x}", label: "Exponential" },
      { id: "cdot", latex: "\\cdot", display: "\\cdot", label: "Dot product" },
      { id: "div", latex: "\\div", display: "\\div", label: "Division" },
      { id: "neq", latex: "\\neq", display: "\\neq", label: "Not equal" },
      { id: "approx", latex: "\\approx", display: "\\approx", label: "Approximately" },
    ],
  },
  {
    id: "greek",
    label: "αβγ Greek",
    symbols: [
      { id: "alpha", latex: "\\alpha", display: "\\alpha", label: "alpha" },
      { id: "beta", latex: "\\beta", display: "\\beta", label: "beta" },
      { id: "gamma", latex: "\\gamma", display: "\\gamma", label: "gamma" },
      { id: "delta", latex: "\\delta", display: "\\delta", label: "delta" },
      { id: "epsilon", latex: "\\epsilon", display: "\\epsilon", label: "epsilon" },
      { id: "lambda", latex: "\\lambda", display: "\\lambda", label: "lambda" },
      { id: "mu", latex: "\\mu", display: "\\mu", label: "mu" },
      { id: "sigma", latex: "\\sigma", display: "\\sigma", label: "sigma" },
      { id: "omega", latex: "\\omega", display: "\\omega", label: "omega" },
      { id: "Delta", latex: "\\Delta", display: "\\Delta", label: "Delta" },
    ],
  },
  {
    id: "calculus",
    label: "Calculus ∫/Σ",
    symbols: [
      { id: "int", latex: "\\int", display: "\\int", label: "Integral" },
      { id: "intdef", latex: "\\int_{}^{}", display: "\\int_{a}^{b}", label: "Definite integral" },
      { id: "ddx", latex: "\\frac{d}{dx}", display: "\\frac{d}{dx}", label: "Derivative" },
      { id: "pdx", latex: "\\frac{\\partial}{\\partial x}", display: "\\frac{\\partial}{\\partial x}", label: "Partial derivative" },
      { id: "lim", latex: "\\lim_{}", display: "\\lim", label: "Limit" },
      { id: "sum", latex: "\\sum", display: "\\sum", label: "Summation" },
      { id: "prod", latex: "\\prod", display: "\\prod", label: "Product" },
      { id: "nabla", latex: "\\nabla", display: "\\nabla", label: "Nabla" },
      { id: "iint", latex: "\\iint", display: "\\iint", label: "Double integral" },
      { id: "oint", latex: "\\oint", display: "\\oint", label: "Contour integral" },
      { id: "coprod", latex: "\\coprod", display: "\\coprod", label: "Coproduct" },
    ],
  },
  {
    id: "trig",
    label: "Trig sin/cos",
    symbols: [
      { id: "sin", latex: "\\sin", display: "\\sin", label: "sine" },
      { id: "cos", latex: "\\cos", display: "\\cos", label: "cosine" },
      { id: "tan", latex: "\\tan", display: "\\tan", label: "tangent" },
      { id: "cot", latex: "\\cot", display: "\\cot", label: "cotangent" },
      { id: "sec", latex: "\\sec", display: "\\sec", label: "secant" },
      { id: "csc", latex: "\\csc", display: "\\csc", label: "cosecant" },
      { id: "arcsin", latex: "\\sin^{-1}", display: "\\sin^{-1}", label: "arcsine" },
      { id: "arccos", latex: "\\cos^{-1}", display: "\\cos^{-1}", label: "arccosine" },
      { id: "arctan", latex: "\\tan^{-1}", display: "\\tan^{-1}", label: "arctangent" },
      { id: "sinh", latex: "\\sinh", display: "\\sinh", label: "hyperbolic sine" },
    ],
  },
  {
    id: "relations",
    label: "Relations",
    symbols: [
      { id: "eq", latex: "=", display: "=", label: "Equals" },
      { id: "neq2", latex: "\\neq", display: "\\neq", label: "Not equal" },
      { id: "lt", latex: "<", display: "\\lt", label: "Less than" },
      { id: "gt", latex: ">", display: "\\gt", label: "Greater than" },
      { id: "leq", latex: "\\leq", display: "\\leq", label: "Less or equal" },
      { id: "geq", latex: "\\geq", display: "\\geq", label: "Greater or equal" },
      { id: "ll", latex: "\\ll", display: "\\ll", label: "Much less" },
      { id: "gg", latex: "\\gg", display: "\\gg", label: "Much greater" },
      { id: "approx2", latex: "\\approx", display: "\\approx", label: "Approximately" },
      { id: "equiv", latex: "\\equiv", display: "\\equiv", label: "Equivalent" },
      { id: "sim", latex: "\\sim", display: "\\sim", label: "Similar" },
      { id: "simeq", latex: "\\simeq", display: "\\simeq", label: "Similar equal" },
      { id: "cong", latex: "\\cong", display: "\\cong", label: "Congruent" },
      { id: "propto", latex: "\\propto", display: "\\propto", label: "Proportional" },
    ],
  },
  {
    id: "matrices",
    label: "Matrices [ ⬚ ]",
    symbols: [
      { id: "mparen", latex: "\\left(  \\right)", display: "\\left( \\square \\right)", label: "Parentheses" },
      { id: "mbrack", latex: "\\left[  \\right]", display: "\\left[ \\square \\right]", label: "Brackets" },
      { id: "mabs", latex: "\\left|  \\right|", display: "\\left| \\square \\right|", label: "Absolute value" },
      { id: "mnorm", latex: "\\left\\|  \\right\\|", display: "\\left\\| \\square \\right\\|", label: "Norm" },
      { id: "mbrace", latex: "\\left\\{  \\right\\}", display: "\\left\\{ \\square \\right\\}", label: "Braces" },
      { id: "square", latex: "\\square", display: "\\square", label: "Placeholder" },
      { id: "colvecT", latex: "[x, y]^{T}", display: "[x, y]^{T}", label: "Column vector transpose" },
      { id: "rowvec", latex: "[x, y]", display: "[x, y]", label: "Row vector" },
      {
        id: "aij",
        latex: "\\begin{bmatrix} a_{11} & a_{12} \\\\ a_{21} & a_{22} \\end{bmatrix}",
        display: "[a_{ij}]",
        label: "Matrix entries",
      },
      { id: "cdots", latex: "\\cdots", display: "\\cdots", label: "Horizontal ellipsis" },
      { id: "vdots", latex: "\\vdots", display: "\\vdots", label: "Vertical ellipsis" },
      { id: "ddots", latex: "\\ddots", display: "\\ddots", label: "Diagonal ellipsis" },
      { id: "AT", latex: "A^{T}", display: "A^{T}", label: "Transpose" },
      { id: "Ainv", latex: "A^{-1}", display: "A^{-1}", label: "Inverse" },
    ],
  },
]

/** Caret offset inside `latex` after inserting a template. */
export function caretAfterInsert(latex: string): number {
  const emptyGroup = latex.indexOf("{}")
  if (emptyGroup >= 0) return emptyGroup + 1
  const emptyOpt = latex.indexOf("[]")
  if (emptyOpt >= 0) return emptyOpt + 1
  const padded = latex.indexOf("  ")
  if (padded >= 0) return padded + 1
  return latex.length
}

/**
 * Wrap editor-popup LaTeX for a real TeX document.
 * `equation` is single-line; a body with `\\` or newlines must sit inside
 * `aligned` or Tectonic fails (`There's no line here to end` / bad tokens).
 */
export function wrapEquationForDocument(body: string): string {
  const trimmed = body.trim()
  if (!trimmed) return ""
  if (/\\begin\{(equation|align|gather|multline|aligned)\*?\}/.test(trimmed)) {
    return trimmed
  }

  const indent = (src: string, spaces: number) => {
    const pad = " ".repeat(spaces)
    return src
      .split("\n")
      .map((line) => (line.length ? `${pad}${line}` : line))
      .join("\n")
  }

  const multiline = trimmed.includes("\n") || /\\\\/.test(trimmed)
  if (multiline) {
    return [
      "\\begin{equation}",
      "  \\begin{aligned}",
      indent(trimmed, 4),
      "  \\end{aligned}",
      "\\end{equation}",
    ].join("\n")
  }

  return `\\begin{equation}\n${indent(trimmed, 2)}\n\\end{equation}`
}

/** Strip display environments so the popup can re-edit an inserted equation. */
export function unwrapEquationDraft(selected: string): string {
  let t = selected.trim()
  if (!t) return ""
  const eq = t.match(/\\begin\{equation\*?\}([\s\S]*?)\\end\{equation\*?\}/)
  if (eq) t = eq[1].trim()
  const aligned = t.match(/\\begin\{aligned\}([\s\S]*?)\\end\{aligned\}/)
  if (aligned) t = aligned[1].trim()
  const align = t.match(/\\begin\{align\*?\}([\s\S]*?)\\end\{align\*?\}/)
  if (align) t = align[1].trim()
  const lines = t.split("\n")
  const indents = lines
    .filter((line) => line.trim())
    .map((line) => line.match(/^ */)?.[0].length ?? 0)
  const min = indents.length ? Math.min(...indents) : 0
  return lines.map((line) => line.slice(min)).join("\n").trim()
}

/**
 * KaTeX cannot render `equation` / `align`. Normalize those so the live
 * preview still typesets the math the user typed.
 */
export function latexForKatexPreview(source: string): string {
  let t = source.trim()
  if (!t) return ""

  t = t
    .replace(/\\begin\{equation\*?\}/g, "")
    .replace(/\\end\{equation\*?\}/g, "")
    .replace(/\\begin\{align\*?\}/g, "\\begin{aligned}")
    .replace(/\\end\{align\*?\}/g, "\\end{aligned}")
    .trim()

  if (!t) return ""
  if (!/\\begin\{/.test(t) && t.includes("\\\\")) {
    return `\\begin{aligned}${t}\\end{aligned}`
  }
  return t
}
